import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAutoTranslate } from '../hooks/useAutoTranslate';
import { Loader2 } from 'lucide-react';

/**
 * AutoTranslatedText - Displays text with automatic translation
 * Falls back to manual translations if available, otherwise uses AI translation
 * 
 * @param {string} text - The original text
 * @param {string} sourceLang - Source language (default: 'en')
 * @param {object} translations - Manual translations object { fr: "...", es: "...", de: "..." }
 * @param {string} className - Additional CSS classes
 * @param {string} as - HTML element to render as (default: 'span')
 * @param {boolean} showLoading - Show loading indicator (default: true)
 */
const AutoTranslatedText = ({
  text,
  sourceLang = 'en',
  translations = null,
  className = '',
  as: Component = 'span',
  showLoading = true,
  ...props
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';

  const { translatedText, isLoading } = useAutoTranslate(
    text,
    sourceLang,
    currentLang,
    translations
  );

  if (isLoading && showLoading) {
    return (
      <Component className={`inline-flex items-center gap-1 ${className}`} {...props}>
        <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
        <span className="text-slate-400 text-sm">Translating...</span>
      </Component>
    );
  }

  return (
    <Component className={className} {...props}>
      {translatedText}
    </Component>
  );
};

/**
 * AutoTranslatedParagraph - For longer text content with "Read More" support
 */
export const AutoTranslatedParagraph = ({
  text,
  sourceLang = 'en',
  translations = null,
  maxLength = 150,
  className = '',
  onReadMore,
  ...props
}) => {
  const { t, i18n, ready } = useTranslation();
  const currentLang = (i18n.language || 'en').split('@')[0];

  const { translatedText, isLoading } = useAutoTranslate(
    text,
    sourceLang,
    currentLang,
    translations
  );

  // Fallback translations for "Read More" in case i18n isn't ready
  const readMoreFallback = {
    en: 'Read More',
    fr: 'Lire la suite',
    es: 'Leer más',
    de: 'Mehr lesen'
  };
  
  const translatingFallback = {
    en: 'Translating...',
    fr: 'Traduction en cours...',
    es: 'Traduciendo...',
    de: 'Übersetzen...'
  };

  const getReadMoreText = () => {
    const translated = t('common.read_more');
    // If translation returns the key, use fallback
    if (translated === 'common.read_more') {
      return readMoreFallback[currentLang] || readMoreFallback.en;
    }
    return translated;
  };

  const getTranslatingText = () => {
    const translated = t('common.translating');
    if (translated === 'common.translating') {
      return translatingFallback[currentLang] || translatingFallback.en;
    }
    return translated;
  };

  if (isLoading) {
    return (
      <div className={`flex items-center gap-2 ${className}`} {...props}>
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-slate-400">{getTranslatingText()}</span>
      </div>
    );
  }

  const needsTruncation = translatedText && translatedText.length > maxLength;
  const displayText = needsTruncation
    ? translatedText.substring(0, maxLength) + '...'
    : translatedText;

  return (
    <div className={className} {...props}>
      <p className="text-slate-600">{displayText}</p>
      {needsTruncation && onReadMore && (
        <button
          onClick={onReadMore}
          className="text-primary hover:text-primary/80 text-sm font-medium mt-1 inline-flex items-center gap-1"
        >
          {getReadMoreText()} →
        </button>
      )}
    </div>
  );
};

export default AutoTranslatedText;
